import { type AuthUser } from "~/api/auth/AuthUser";
import { FEATURE_FLAGS } from "~/feature-flags";
import {
  type FeatureFlagState,
  type FeatureFlagLocalKey,
  type FeatureFlagForeignKey,
} from "~/feature-flags-defaults";

export const FEATURE_FLAGS_STORAGE_KEY = "di_internal_feature_flags";

export const applyUserOverrideFeatureFlags = (
  user: AuthUser,
  state: FeatureFlagState,
): FeatureFlagState => {
  if (!user) return state;

  return Object.keys(state).reduce((final, key) => {
    const foreignKey: FeatureFlagForeignKey =
      FEATURE_FLAGS[key as FeatureFlagLocalKey];

    if (user.featureFlags.includes(foreignKey)) {
      final[key as FeatureFlagLocalKey] = true;
    }
    return final;
  }, state);
};

export const applyLocalOverrideFeatureFlags = (state: FeatureFlagState) => {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!stored) return state;

    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const result: FeatureFlagState = { ...state };

    // Only copy valid boolean values for known keys
    for (const key in result) {
      if (key in parsed && typeof parsed[key] === "boolean") {
        result[key as FeatureFlagLocalKey] = parsed[key];
      }
    }

    console.debug(
      `Applying feature-flags from ${FEATURE_FLAGS_STORAGE_KEY}`,
      result,
    );

    return result;
  } catch (err) {
    console.error(
      `Getting local FeatureFlags overrides from "${FEATURE_FLAGS_STORAGE_KEY}" failed.`,
      err,
    );
  }

  return state;
};

export const applyAllOverrideFeatureFlags = (
  user: AuthUser,
  state: FeatureFlagState,
) => {
  const withUserFlags = applyUserOverrideFeatureFlags(user, state);

  // An impersonated session must show what the customer sees, so the staff
  // user's own local overrides are left out of the resolution.
  if (user?.isImpersonated) return withUserFlags;

  return applyLocalOverrideFeatureFlags(withUserFlags);
};
